/**
 * One row of the Inbox's Queue (#82): an item the AI will get to, or is on right now. It has no
 * form and no Create task or Dismiss — it is about to leave on its own, and a form left open on
 * it is exactly what the worker used to file out from under the person typing.
 *
 * The row says where the item stands. Waiting: a plain line, plus the reason the last try failed
 * if it did. Picked up: the row pulses and the lightbulb starts its usual in-place answer
 * ("Asking the AI…"), driven by the worker's run rather than a click, and the answer comes back
 * the way a retry's does — "Created Task: … — in Cinema", held until it is cleared or the
 * Display setting's auto-hide clears it, then the row leaves. The bulb is also a way to run the
 * item now, at its own tier.
 */

import { useState } from "react";
import type { CaptureTier, InboxAiRetryResult, InboxItem } from "../api";
import { useCaptureTier } from "../captureTierContext";
import { openFiled, useEditPopups } from "../editPopupContext";
import { InboxAiRetryButton } from "./InboxAiRetryButton";
import { Clock } from "./icons";
import { captureTimeLabel } from "./taskFormat";

export function InboxQueueRow({
  item,
  watching,
  onRunNow,
  onAwaitResult,
  onSettled,
}: {
  item: InboxItem;
  /** The worker has this item (or has just finished with it): follow its run in the bulb. */
  watching: boolean;
  /** Runs the item now, at the bulb's tier — the same call as a retry. */
  onRunNow: (item: InboxItem, tier: CaptureTier | null) => Promise<InboxAiRetryResult>;
  /** Resolves with the worker's answer once it has one. */
  onAwaitResult: (item: InboxItem) => Promise<InboxAiRetryResult>;
  onSettled: (item: InboxItem, result: InboxAiRetryResult | null) => void | Promise<void>;
}) {
  // The bulb is not a new control here but the one on the Add card (or the account's capture tier,
  // for a watch or CLI capture, which is stamped with it on arrival) carried over: it shows the
  // tier the item was added under. A pick on it changes this item's run and nothing else.
  const { tier: accountTier } = useCaptureTier();
  const popups = useEditPopups();
  const [picked, setPicked] = useState<CaptureTier | null>(null);
  const tier = picked ?? item.tier ?? accountTier;
  const active = watching || item.state === "processing";
  const status = active
    ? "Picked up by the AI"
    : item.last_error
      ? `The last try failed: ${item.last_error} It will try again.`
      : "Waiting for the AI";

  return (
    <li
      className={active ? "task inbox-row inbox-queue-row is-processing" : "task inbox-row inbox-queue-row"}
      data-testid="inbox-queue-item"
      aria-busy={active}
    >
      <span className="title">
        <span className="text">{item.raw_text}</span>
        <span className="meta">
          <span className="badge">{item.source}</span>
          <span className="badge" title="When this was captured">
            <Clock size={12} />
            {captureTimeLabel(item.created_at, item.client_context?.captured_at)}
          </span>
          <span className="muted">{status}</span>
        </span>
      </span>
      <span className="task-actions">
        <InboxAiRetryButton
          tierControl={{ tier, onChange: setPicked }}
          onEdit={(created) => openFiled(popups, created.kind, created.id)}
          autoStart={active}
          // The row stays put when a failed try is going to be retried; otherwise the item has
          // left the queue (filed, or handed to a person) and so does the row.
          leaveOnClear={(result) => result.outcome !== "failed" || result.item.state !== "queued"}
          onRetry={(tier) => (active ? onAwaitResult(item) : onRunNow(item, tier))}
          onSettled={(result) => onSettled(item, result)}
        />
      </span>
    </li>
  );
}
